import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const getSessionId = (key) => {
  let id = localStorage.getItem(key);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    localStorage.setItem(key, id);
  }
  return id;
};

export const TutorChat = ({ publicMode = false }) => {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const storageKey = publicMode ? "urcal_tutor_public" : `urcal_tutor_${user?.id || "anon"}`;
  const sessionId = getSessionId(`${storageKey}_session`);

  useEffect(() => {
    const saved = localStorage.getItem(`${storageKey}_msgs`);
    if (saved) {
      try { setMessages(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
  }, [storageKey]);

  useEffect(() => {
    if (messages.length) {
      localStorage.setItem(`${storageKey}_msgs`, JSON.stringify(messages.slice(-40)));
    }
  }, [messages, storageKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API_URL}/tutor/chat${publicMode ? "/public" : ""}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(publicMode || !token ? {} : { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ session_id: sessionId, message: text })
      });

      if (!res.ok || !res.body) throw new Error("Respuesta no válida");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + payload.delta
              };
              return next;
            });
          }
          if (payload.error) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: payload.error };
              return next;
            });
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "No he podido conectar con el tutor. Inténtalo de nuevo en unos minutos."
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, publicMode, token, sessionId]);

  return (
    <div className="fixed bottom-24 right-6 z-[60]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-20 right-0 w-[92vw] max-w-md rounded-2xl overflow-hidden border border-[#D4AF37]/30 bg-[#0A1128] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
            data-testid="tutor-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#111827]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[#0A1128]" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Tutor Virtual</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> IA · Disponible 24/7
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="tutor-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="h-[380px] overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center pt-16 px-6">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center">
                    <Bot className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <p className="text-white font-semibold mb-2">¡Hola! Soy tu Tutor Virtual</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {publicMode
                      ? "Pregúntame sobre el campus, la formación o cualquier duda de consultoría y freelancing."
                      : "Pregúntame dudas sobre consultoría, freelancing, tu negocio o tus cursos."}
                  </p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`tutor-message-${i}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === "user"
                        ? "gold-gradient text-[#0A1128] font-medium rounded-br-sm"
                        : "bg-white/8 border border-white/10 text-slate-200 rounded-bl-sm"
                    }`}
                  >
                    {msg.content || (
                      <span className="flex items-center gap-2 text-slate-400">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pensando...
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-[#111827]">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Escribe tu pregunta..."
                  className="flex-1 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
                  data-testid="tutor-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={streaming || !input.trim()}
                  className="w-11 h-11 rounded-xl gold-gradient text-[#0A1128] flex items-center justify-center disabled:opacity-40 btn-active transition-opacity"
                  data-testid="tutor-send-btn"
                >
                  {streaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="w-14 h-14 rounded-full gold-gradient flex items-center justify-center shadow-[0_0_25px_rgba(212,175,55,0.45)]"
        data-testid="tutor-open-btn"
        aria-label="Tutor Virtual"
      >
        {open ? <X className="w-6 h-6 text-[#0A1128]" /> : <Bot className="w-6 h-6 text-[#0A1128]" />}
      </motion.button>
    </div>
  );
};
